import { fitDraft, footprint } from '../shared/paint';
import type { Asset, CommitResult, Draft, PaintOrder, PlacementInput, Surface } from '../shared/types';

export interface PreparedWallCommit { asset: Asset; draft: Draft; cost: number }
export type WallCommitOutcome =
  | { kind: 'over-budget' | 'blank'; cost: number }
  | { kind: 'committed'; result: CommitResult; cost: number };
export interface WallCommitOptions {
  surface: Surface;
  displayedInk: number;
  balance: number;
  unlimitedInk?: boolean;
  paintOrder?: PaintOrder;
  belowPlacementId?: string;
  intent?:import('../shared/types').EraseIntent;
  blob?: Blob;
  getBlob?: () => Promise<Blob | null>;
  upload: (blob: Blob) => Promise<Asset>;
  commit: (input: PlacementInput) => Promise<CommitResult>;
  key: () => string;
}

/** A rounded backing canvas can differ slightly from the wall's aspect. Cover
 * the wall, centred, using the normalized artwork's exact aspect and clipping. */
export function alignedWallDraft(surface: Surface, asset: Asset): Draft {
  const ratio = asset.width / asset.height;
  const width = Math.max(surface.width, surface.height * ratio), height = width / ratio;
  return fitDraft(surface, asset, (surface.width - width) / 2, (surface.height - height) / 2, Math.max(width, height));
}

/** Owns only preparation and confirmation. The caller retains the editable
 * canvas until acknowledgement and invalidates preparation when pixels change. */
export class WallCommit {
  private artwork: Readonly<PreparedWallCommit> | null = null;
  private attempt: Readonly<PlacementInput> | null = null;
  private interrupted = false;
  private flight: Promise<WallCommitOutcome> | null = null;

  get prepared() { return this.artwork; }
  get cost() { return this.artwork?.cost ?? null; }
  get uncertain() { return this.interrupted; }

  invalidate(): boolean {
    if (this.interrupted || this.flight) return false;
    this.artwork = null;
    this.attempt = null;
    return true;
  }

  confirm(options: WallCommitOptions): Promise<WallCommitOutcome> {
    if (this.flight) return this.flight;
    const flight = this.run(options).finally(() => { this.flight = null; });
    this.flight = flight;
    return flight;
  }

  private async run(options: WallCommitOptions): Promise<WallCommitOutcome> {
    if (!this.artwork) {
      const blob = options.blob ?? await options.getBlob?.();
      if (!blob) throw new Error('Finish your stroke before painting.');
      const uploaded = await options.upload(blob);
      const mask = { ...uploaded.mask, alpha: [...uploaded.mask.alpha] };
      Object.freeze(mask.alpha); Object.freeze(mask);
      const asset = Object.freeze({ ...uploaded, mask });
      const draft = Object.freeze({ ...alignedWallDraft(options.surface, asset), paintOrder: options.paintOrder ?? 'over', ...(options.belowPlacementId ? { belowPlacementId: options.belowPlacementId } : {}) });
      this.artwork = Object.freeze({ asset, draft, cost: footprint(options.surface, asset, draft).cost });
    }
    const prepared = this.artwork;
    if (!this.interrupted) {
      if (prepared.draft.surfaceId !== options.surface.id) throw new Error('The drawing wall changed. Prepare the current drawing again.');
      if (prepared.draft.paintOrder !== (options.paintOrder ?? 'over')) throw new Error('The paint order changed. Prepare the current drawing again.');
      if (prepared.draft.belowPlacementId !== options.belowPlacementId) throw new Error('The layer position changed. Prepare the current drawing again.');
      if (![options.displayedInk, options.balance].every(value => Number.isFinite(value) && value >= 0)) throw new Error('Wait for a valid ink estimate and wallet before painting.');
      if (prepared.cost === 0) return { kind: 'blank', cost: 0 };
      // Paint authorizes the final normalized price; the preview remains an estimate.
      if (!options.unlimitedInk && prepared.cost > options.balance) return { kind: 'over-budget', cost: prepared.cost };
      this.attempt ??= Object.freeze({ ...prepared.draft,...options.intent, idempotencyKey: options.key() });
    }
    // An interrupted request may already be paid. Its exact replay must not be
    // blocked by a changed wallet, account, estimate or current editor geometry.
    const pending = this.attempt;
    if (!pending) throw new Error('The interrupted painting request is missing.');
    try {
      const result = await options.commit(pending);
      this.interrupted = false;
      // Keep the key until the acknowledged canvas is discarded/edited. A
      // duplicate confirmation during UI cleanup still replays the paid mark.
      return { kind: 'committed', result, cost: prepared.cost };
    } catch (failure) {
      const status = failure instanceof Error && 'status' in failure ? failure.status : undefined;
      this.interrupted = !(typeof status === 'number' && status >= 400 && status < 500);
      if (!this.interrupted) this.attempt = null;
      throw failure;
    }
  }
}
